import { TrailHistoryEntry, DTO_TrailStop } from "../services/calls/types/mobiles"
import { Coordinate } from "ol/coordinate"
import { DateTime } from "luxon"
import { ReactNode } from "react"
import { renderToString } from "react-dom/server"
import { ICON } from "../components/Icon/data"
import Icon from "../components/Icon"
import RoadIcon from "../components/RoadIcon"
import { COLOR } from "../data/variables"

const EARTH_RADIUS = 6371000
const ICELAND_CROSS = 500000

export type Speed = {
  id: number
  speedlimit: number
  speed: number
  date: string
}

export type Trail = {
  id: number | string
  date: string
  heading: number
  lat: number
  lon: number
  location: string
}

export type StopTrail = Trail & {
  from_date?: string
  to_date?: string
  duration: number
  mobile: string
}

export type TrailInfo = {
  speed: Speed[]
  trail: Trail[]
  stops: StopTrail[]
}

export const useMap = () => {
  const radiansToDegrees = (radians: number) => {
    return radians * (180 / Math.PI)
  }

  const degreesToRadians = (degrees: number) => {
    return degrees / (180 / Math.PI)
  }

  const milesToKilometers = (mps: number) => {
    return parseInt(Math.round(mps * 3.6))
  }

  const calculateIconScale = (zoom: number) => {
    const newMin = 0
    const newMax = 1
    const minZoom = 6
    const maxZoom = 18

    // convert the scale (6 - 18) to (0 - 1)
    const scale = (((zoom - minZoom) / (maxZoom - minZoom)) * (newMax - newMin) + newMin)
    return Math.max(scale, 0.4)
  }


  const encodeSVG = (icon: ReactNode) => {
    return `data:image/svg+xml;utf8,${encodeURIComponent(renderToString(icon))}`
  }

  type PointStyleOptions = {
    heading?: number
    color?: string | number[]
    scale?: number
  }

  const defaultPointOptions: PointStyleOptions = {
    heading: 0,
    color: COLOR.MARKER_BLUE,
    scale: 50
  }

  const getPointStyle = (
    zoom: number, icon: string = ICON.MAP_MARKER, isRoadIcon: boolean = false, propOptions = {}
  ) => {
    const options = { ...defaultPointOptions, ...propOptions }
    const iconElem = isRoadIcon ? <RoadIcon icon={icon} size={50} /> : <Icon icon={icon} color={options.color} size={options.scale} />
    const src = encodeSVG(iconElem)

    return {
      src,
      rotation: options.heading,
      scale: calculateIconScale(zoom)
    }
  }

  const calculateZoomScale = (distance: number, minZoom: number = 6, maxZoom: number = 18) => {
    return maxZoom - ((distance / ICELAND_CROSS) * (maxZoom - minZoom))
  }

  const calculateDistance = (coord1: Coordinate, coord2: Coordinate) => {
    const toRad = (deg: number) => (deg * Math.PI) / 180

    const [lon1, lat1] = coord1
    const [lon2, lat2] = coord2

    const dLat = toRad(lat2 - lat1)
    const dLon = toRad(lon2 - lon1)

    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) ** 2

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

    return EARTH_RADIUS * c // in meters
  }

  const extractHistory = (history: TrailHistoryEntry[], stops: DTO_TrailStop): Record<string, TrailInfo> => {
    const lookup: Record<string, TrailInfo> = {}
    history.forEach((h) => {
      const {
        mobile_globalid, speedlimit, date, heading, lat, lon, trailid,
        speed, location
      } = h

      if (typeof lookup[mobile_globalid] === 'undefined') {
        lookup[mobile_globalid] = {
          speed: [],
          trail: [],
          stops: []
        }
      }
      const tempSpeed = {
        id: trailid,
        speedlimit: milesToKilometers(speedlimit),
        speed: milesToKilometers(speed),
        date
      }
      lookup[mobile_globalid].speed.push(tempSpeed)

      const tempTrail = {
        id: trailid,
        date,
        heading,
        lat,
        lon,
        location
      }
      if (stops[trailid]) {
        const duration = stops[trailid]
        const tempStop = {
          ...tempTrail,
          mobile: mobile_globalid,
          from_date: date,
          to_date: DateTime.fromISO(date).plus({ minute: duration}).toISO(),
          duration,
        }
        lookup[mobile_globalid].stops.push(tempStop)
      }
      lookup[mobile_globalid].trail.push(tempTrail)
    })
    return lookup
  }

  return {
    radiansToDegrees,
    degreesToRadians,
    extractHistory,
    getPointStyle,
    milesToKilometers,
    calculateDistance,
    calculateZoomScale,
    calculateIconScale
  }
}
